import { useEffect, useRef, useState } from "react";
import { Trophy, ArrowLeft, MapPin, Crown } from "lucide-react";
import confetti from "canvas-confetti";

import { Button } from "./ui/button";

const TOP_LIMIT = 10;

function Row({ row, idx, isVoted, max, isTop, boosting }) {
  const pct = Math.max(4, (row.votes / max) * 100);
  return (
    <div
      data-testid={`leaderboard-row-${idx}`}
      className={`relative rounded-xl ${
        isVoted ? "ring-2 ring-[#2e6c2e]" : "ring-1 ring-black/8"
      } bg-white overflow-hidden ${boosting ? "boost-pulse" : ""}`}
    >
      <div
        className="bar-fill absolute inset-y-0 left-0 transition-[width] duration-700 ease-out"
        style={{
          width: `${pct}%`,
          background:
            "linear-gradient(90deg, rgba(46,108,46,0.18) 0%, rgba(46,108,46,0.10) 70%, rgba(46,108,46,0.04) 100%)",
        }}
      />
      <div className="relative flex items-center justify-between gap-3 px-3 py-2.5">
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`font-display text-xl leading-none ${
              isTop ? "brand-green" : "text-black/35"
            } w-7 shrink-0 tabular-nums`}
          >
            {String(idx + 1).padStart(2, "0")}
          </span>
          {isTop && <Trophy className="w-4 h-4 brand-green shrink-0" />}
          <div className="min-w-0">
            <p className="font-semibold text-sm sm:text-base leading-tight truncate flex items-center gap-2">
              <span className="truncate">{row.city}</span>
              {isVoted && (
                <span className="text-[9px] font-medium uppercase tracking-wider brand-green px-1.5 py-0.5 rounded-full bg-[#2e6c2e]/10 shrink-0">
                  Twój głos
                </span>
              )}
            </p>
            {(row.skatepark || row.region) && (
              <p className="text-[10px] sm:text-[11px] text-black/45 truncate">
                {row.skatepark}
                {row.skatepark && row.region && " · "}
                {row.region}
              </p>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display text-xl sm:text-2xl leading-none brand-green tabular-nums">
            {row.votes}
          </p>
          <p className="text-[9px] sm:text-[10px] text-black/45 tabular-nums">{row.percentage}%</p>
        </div>
      </div>
    </div>
  );
}

function tinyConfettiAt(targetEl) {
  if (!targetEl) return;
  const rect = targetEl.getBoundingClientRect();
  const x = (rect.left + rect.right) / 2 / window.innerWidth;
  const y = (rect.top + rect.bottom) / 2 / window.innerHeight;
  confetti({
    particleCount: 32,
    spread: 55,
    startVelocity: 22,
    scalar: 0.65,
    ticks: 90,
    gravity: 0.9,
    origin: { x, y },
    colors: ["#2e6c2e", "#5fa15f", "#94d094", "#ffffff"],
    disableForReducedMotion: true,
  });
}

export default function ResultsPanel({ title = "Ranking", items, totalVotes, onBack, votedId, votingEnded = false }) {
  const all = items || [];
  const top = all.slice(0, TOP_LIMIT);
  const max = top[0]?.votes || 1;
  const winner = top[0] || null;
  const votedRow = votedId ? all.find((r) => r.skatepark_id === votedId) : null;
  const votedRank = votedRow ? all.indexOf(votedRow) + 1 : null;
  const votedOutsideTop = votedRow && votedRank > TOP_LIMIT;

  // Detect newly-incremented row → trigger boost pulse + tiny confetti
  const prevVotesRef = useRef(new Map());
  const [boostingId, setBoostingId] = useState(null);
  const rowsRef = useRef({});

  useEffect(() => {
    const prev = prevVotesRef.current;
    let boosted = null;
    for (const r of all) {
      const old = prev.get(r.skatepark_id);
      if (old !== undefined && r.votes > old) {
        boosted = r.skatepark_id;
        break;
      }
    }
    // Update snapshot
    prevVotesRef.current = new Map(all.map((r) => [r.skatepark_id, r.votes]));
    if (boosted) {
      setBoostingId(boosted);
      // tiny confetti
      const el = rowsRef.current[boosted];
      tinyConfettiAt(el);
      const t = setTimeout(() => setBoostingId(null), 1500);
      return () => clearTimeout(t);
    }
  }, [all]);

  return (
    <div data-testid="results-panel" className="w-full text-left">
      <div className="flex items-end justify-between mb-4">
        <div>
          <p
            className={`text-[10px] font-medium uppercase tracking-[0.25em] ${
              votingEnded ? "text-red-600" : "brand-green"
            }`}
          >
            {votingEnded ? "Głosowanie zakończone" : "Wyniki · Live"}
          </p>
          <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-none mt-1">
            {title}
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-medium uppercase tracking-wider text-black/40">Głosów</p>
          <p
            data-testid="leaderboard-total"
            className="font-display text-3xl brand-green leading-none tabular-nums"
          >
            {totalVotes ?? 0}
          </p>
        </div>
      </div>

      {votingEnded && winner && (
        <div
          data-testid="winner-banner"
          className="rounded-2xl p-4 sm:p-5 mb-3 ring-2 ring-[#2e6c2e] relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #ffffff 0%, #ecf6ec 60%, #d8ebd8 100%)",
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <Crown className="w-5 h-5 brand-green" />
            <p className="text-[10px] font-medium uppercase tracking-[0.3em] brand-green">
              Zwycięża
            </p>
          </div>
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight leading-none truncate">
                {winner.city}
              </h2>
              {(winner.skatepark || winner.region) && (
                <p className="text-[11px] sm:text-xs text-black/55 mt-1.5 truncate">
                  {winner.skatepark}
                  {winner.skatepark && winner.region && " · "}
                  {winner.region}
                </p>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="font-display brand-green tabular-nums leading-none text-3xl sm:text-4xl">
                {winner.votes}
              </p>
              <p className="text-[10px] text-black/50 tabular-nums mt-1">
                {winner.percentage}% wszystkich
              </p>
            </div>
          </div>
        </div>
      )}

      {top.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-black/10 p-8 text-center">
          <p className="text-black/50 text-sm">Brak głosów — bądź pierwszy!</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {top.map((row, idx) => (
            <div
              key={row.skatepark_id}
              ref={(el) => {
                if (el) rowsRef.current[row.skatepark_id] = el;
              }}
            >
              <Row
                row={row}
                idx={idx}
                isVoted={row.skatepark_id === votedId}
                isTop={idx === 0}
                max={max}
                boosting={boostingId === row.skatepark_id}
              />
            </div>
          ))}
        </div>
      )}

      {votedOutsideTop && (
        <div
          data-testid="voted-outside-top"
          className={`mt-3 rounded-xl ring-2 ring-[#2e6c2e] bg-[#f4faf4] px-3 py-2.5 ${
            boostingId === votedRow.skatepark_id ? "boost-pulse" : ""
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <MapPin className="w-4 h-4 brand-green shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-sm leading-tight truncate flex items-center gap-2">
                  <span className="truncate">{votedRow.city}</span>
                  <span className="text-[9px] font-medium uppercase tracking-wider brand-green px-1.5 py-0.5 rounded-full bg-[#2e6c2e]/10 shrink-0">
                    Twój głos
                  </span>
                </p>
                {(votedRow.skatepark || votedRow.region) && (
                  <p className="text-[10px] text-black/45 truncate">
                    {votedRow.skatepark}
                    {votedRow.skatepark && votedRow.region && " · "}
                    {votedRow.region}
                  </p>
                )}
                <p className="text-[10px] text-black/55 mt-0.5">
                  Pozycja #{votedRank} — poza TOP {TOP_LIMIT}, ale walczy o awans!
                </p>
              </div>
            </div>
            <div className="text-right shrink-0">
              <p className="font-display text-xl leading-none brand-green tabular-nums">
                {votedRow.votes}
              </p>
              <p className="text-[9px] text-black/45 tabular-nums">{votedRow.percentage}%</p>
            </div>
          </div>
        </div>
      )}

      {onBack && (
        <div className="mt-5 flex justify-center">
          <Button
            type="button"
            data-testid="back-to-vote-btn"
            onClick={onBack}
            className="btn-ghost-brand gap-2 h-10 px-5 text-xs font-medium tracking-wide"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Zagłosuj ponownie
          </Button>
        </div>
      )}
    </div>
  );
}
